'use client';

import React, { ReactNode, useMemo } from 'react';
import { Button, ButtonProps } from '@mui/material';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { borderRadius, color, opacity, spacing } from '@/theme/tokens';

interface ButtonContainedBrandProps extends ButtonProps {
    loading?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    size?: 'large' | 'medium';
}

const StyledButton = styled(Button, {
    shouldForwardProp: prop => prop !== 'loading',
})<{
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    loading?: boolean;
    size?: 'large' | 'medium';
}>`
    text-transform: none;
    color: ${color.white[0]};
    height: ${({ size }) => (size === 'large' ? '46px' : '40px')};
    background: ${color.brand[50]};
    padding: 0
        ${({ endIcon, startIcon, size }) => (endIcon ? spacing[12] : size === 'large' ? spacing[20] : spacing[16])} 0
        ${({ startIcon, endIcon, size }) => (startIcon ? spacing[12] : size === 'large' ? spacing[20] : spacing[16])};
    display: inline-flex;
    border-radius: ${borderRadius[8]};
    box-shadow: 0 0 0 2px transparent;
    position: relative;

    &:hover {
        background-color: ${color.brand[60]};
    }

    &:active {
        background-color: ${color.brand[70]};
    }

    &.Mui-focusVisible {
        outline: 2px solid ${color.brand[60]};
        outline-offset: 2px;
    }

    &.Mui-disabled {
        opacity: ${({ loading }) => (loading ? 1 : opacity[40])};
        color: ${color.white[0]};
    }
`;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.svg<{ position?: 'start' | 'end' }>`
    animation: ${rotate} 1s linear infinite;
    transform-origin: center;
    margin-left: ${({ position }) => (position === 'end' ? '0' : spacing[6])};
    margin-right: ${({ position }) => (position === 'end' ? spacing[2] : '0')};
`;

const SPINNER_PATH =
    'M24 2C28.3512 2 32.6047 3.29028 36.2225 5.70767C39.8404 8.12506 42.6602 11.561 44.3253 15.581C45.9905 19.6009 46.4262 24.0244 45.5773 28.292C44.7284 32.5596 42.6331 36.4796 39.5563 39.5563C36.4796 42.6331 32.5596 44.7284 28.292 45.5773C24.0244 46.4262 19.6009 45.9905 15.581 44.3253C11.561 42.6602 8.12506 39.8404 5.70767 36.2225C3.29028 32.6047 2 28.3512 2 24';

const createSpinner = (position: 'start' | 'end') => (
    <LoadingSpinner
        position={position}
        width="18"
        height="18"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d={SPINNER_PATH} stroke="white" strokeWidth="4" />
    </LoadingSpinner>
);

export default function ButtonContainedBrand({
    children,
    disabled,
    loading = false,
    onClick,
    startIcon,
    endIcon,
    size = 'large',
    ...rest
}: ButtonContainedBrandProps) {
    const { finalStartIcon, finalEndIcon } = useMemo(() => {
        if (!loading) {
            return { finalStartIcon: startIcon, finalEndIcon: endIcon };
        }

        if (startIcon) {
            return { finalStartIcon: createSpinner('start'), finalEndIcon: endIcon };
        }

        if (endIcon) {
            return { finalStartIcon: undefined, finalEndIcon: createSpinner('end') };
        }

        return { finalStartIcon: createSpinner('start'), finalEndIcon: undefined };
    }, [loading, startIcon, endIcon]);

    return (
        <StyledButton
            disabled={disabled || loading}
            onClick={onClick}
            disableRipple
            loading={loading}
            startIcon={finalStartIcon}
            endIcon={finalEndIcon}
            size={size}
            {...rest}
        >
            {children}
        </StyledButton>
    );
}
